import React, { useEffect, useState } from "react";
import { PlusCircle, Edit, Settings } from "react-feather";
import DataTable from "../../../components/common/DataTable";
import InfoTile from "../../../components/common/InfoTile";
import { MAROON } from "../../../components/common/brandColors";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useGetFuelCardsQuery } from "../fuelApi";
import { useCanManageFuelCards } from "../useFuelPermissions";
import { formatDate, litres, money } from "../fuelFormat";
import FuelCardFormModal from "../components/FuelCardFormModal";
import FuelCardManageModal from "../components/FuelCardManageModal";

// "12,000.00 of 50,000.00" with a thin bar, or just the figure when the card has no limit.
const SpendVsLimit = ({ spent, limit, format }) => {
  if (limit == null) return <>{format(spent || 0)}<div className="small text-muted">no limit</div></>;
  const ratio = Number(limit) > 0 ? Math.min(Number(spent || 0) / Number(limit), 1) : 0;
  const over = Number(spent || 0) > Number(limit);
  return (
    <div style={{ minWidth: 130 }}>
      <span className={over ? "fw-semibold" : undefined} style={over ? { color: "#9A5B00" } : undefined}>{format(spent || 0)}</span>
      <span className="text-muted small"> of {format(limit)}</span>
      <div className="progress mt-1" style={{ height: 4 }}>
        <div className="progress-bar" style={{ width: `${ratio * 100}%`, background: over ? "#F0B95B" : MAROON }} />
      </div>
    </div>
  );
};

const validityText = (card) => {
  if (!card.validFrom && !card.validUntil) return "-";
  return `${card.validFrom ? formatDate(card.validFrom) : "…"} - ${card.validUntil ? formatDate(card.validUntil) : "…"}`;
};

// Fuel Cards master. Readable with any office fuel permission; adding, editing
// and linking need Fuel.ManageCards. The balance shown is moved only by approved
// refills and recorded purchases - never edited here.
const FuelCardsPage = () => {
  const { selectedBranchId } = useBranchFilter();
  const canManage = useCanManageFuelCards();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [managingCardId, setManagingCardId] = useState(null);

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: cards = [], isLoading, isFetching } = useGetFuelCardsQuery({
    branchId: selectedBranchId ?? undefined,
    includeInactive: includeInactive || undefined,
    search: search || undefined,
  });

  const totalBalance = cards.reduce((sum, c) => sum + Number(c.currentBalance || 0), 0);
  const monthSpend = cards.reduce((sum, c) => sum + Number(c.monthAmount || 0), 0);
  const today = new Date();

  const columns = [
    {
      title: "Card",
      dataIndex: "cardNumber",
      render: (v, card) => (
        <>
          <span className="fw-semibold">{v}</span>
          <div className="small text-muted">
            {card.provider}{card.cardType ? ` · ${card.cardType}` : ""} · {card.branchCode}
          </div>
        </>
      ),
    },
    { title: "Fuel type", dataIndex: "fuelType", render: (v) => v || <span className="text-muted">Any</span> },
    {
      title: "Vehicles",
      dataIndex: "vehicleRegNos",
      render: (v, card) =>
        card.vehicleCount > 0 ? (
          <span title={v} style={{ display: "inline-block", maxWidth: 240 }} className="text-truncate">{v}</span>
        ) : (
          <span className="text-muted">none linked</span>
        ),
    },
    { title: "Users", dataIndex: "userCount", render: (v) => v || "-" },
    {
      title: "Balance",
      dataIndex: "currentBalance",
      align: "right",
      render: (v) => (
        <span className="fw-semibold" style={Number(v) < 0 ? { color: "#9A5B00" } : undefined}>{money(v)}</span>
      ),
    },
    {
      title: "This month (amount)",
      dataIndex: "monthAmount",
      render: (v, card) => <SpendVsLimit spent={v} limit={card.monthlyAmountLimit} format={money} />,
    },
    {
      title: "This month (litres)",
      dataIndex: "monthLitres",
      render: (v, card) => <SpendVsLimit spent={v} limit={card.monthlyLitreLimit} format={litres} />,
    },
    {
      title: "Validity",
      dataIndex: "validUntil",
      render: (v, card) => (
        <>
          {validityText(card)}
          {v && new Date(v) < today && <div className="small" style={{ color: "#9A5B00" }}>Expired</div>}
        </>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (v) => <span className={`badge ${v ? "badge-linesuccess" : "badge-linedanger"}`}>{v ? "Active" : "Inactive"}</span>,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, card) => (
        <div className="edit-delete-action d-flex align-items-center">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary me-2 d-inline-flex align-items-center gap-1"
            onClick={() => setManagingCardId(card.fuelCardId)}
          >
            <Settings size={14} /> {canManage ? "Manage" : "Open"}
          </button>
          {canManage && (
            <button type="button" className="btn btn-link p-2" title="Edit card" onClick={() => setEditingCard(card)}>
              <Edit size={16} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Fuel Cards</h4>
              <h6>Prepaid fuel cards, the vehicles and people who use them, and what is left on each</h6>
            </div>
          </div>
          {canManage && (
            <div className="page-btn">
              <button type="button" className="btn btn-added" onClick={() => setShowCreate(true)}>
                <PlusCircle className="me-2" size={16} />
                Add Fuel Card
              </button>
            </div>
          )}
        </div>

        <div className="row g-2 mb-3">
          <div className="col-6 col-md-3"><InfoTile label="Cards" value={cards.length} /></div>
          <div className="col-6 col-md-3"><InfoTile label="Total balance on cards" tone="maroon" value={money(totalBalance)} /></div>
          <div className="col-6 col-md-3"><InfoTile label="Spent this month" value={money(monthSpend)} /></div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top flex-wrap gap-2">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Card number or provider"
                    className="form-control form-control-sm formsearch"
                    style={{ minWidth: 220, height: 34 }}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
              <div className="form-check form-switch mb-0">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="fuelCardsIncludeInactive"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="fuelCardsIncludeInactive">Include inactive</label>
              </div>
            </div>
            <div className="table-responsive">
              <DataTable columns={columns} dataSource={cards} rowKey="fuelCardId" loading={isLoading || isFetching} />
            </div>
          </div>
        </div>
      </div>

      <FuelCardFormModal show={showCreate} onClose={() => setShowCreate(false)} />
      <FuelCardFormModal show={Boolean(editingCard)} card={editingCard} onClose={() => setEditingCard(null)} />
      <FuelCardManageModal fuelCardId={managingCardId} canManage={canManage} onClose={() => setManagingCardId(null)} />
    </div>
  );
};

export default FuelCardsPage;
